import clsx from 'clsx'
import type { ReactElement } from 'react'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import { useApplicantPictures } from '~/domains/applicants/queries/applicantPictures'
import type { ApplicantPicture } from '~/domains/applicants/types'
import { useConfig } from '~/domains/configuration/queries/config'
import { useContactsPictures } from '~/domains/contacts/queries/contactPictures'
import { useUsersPicture } from '~/domains/users/queries/useUsersPicture'
import type { UserPicture } from '~/domains/users/types'
import { profileColorClasses } from '~/styles/profileColors.css'
import { formatName } from '~/utils/names'
import { getProfileColorIndex } from '~/utils/profileColors'
import {
  profilesListItem,
  profilesListItemInitials,
  profilesListItemInitialsNeutral,
  profilesListItemPicture,
  profilesListWrapper,
  profilesListWrapperLeft,
  profilesListWrapperRight
} from './ProfilesList.css'

export type ProfileHeadPicture = ApplicantPicture | UserPicture

export type ProfilesListProps = {
  profilePictures?: ProfileHeadPicture[]
  applicantIds?: number[]
  contactIds?: number[]
  userIds?: number[]
  className?: string
  tooltipText?: string
  /** Skip the names tooltip (when the parent already explains who is shown). */
  noTooltip?: boolean
  maxProfilesCount?: number
  align?: 'center' | 'left' | 'right'
}

export const ProfilesList = ({
  profilePictures,
  applicantIds = [],
  contactIds = [],
  userIds = [],
  className,
  tooltipText,
  noTooltip = false,
  maxProfilesCount = 30,
  align = 'center'
}: ProfilesListProps): ReactElement => {
  const { data: legacyUrl } = useConfig('organization.legacy_url')

  const applicantPictures = useApplicantPictures({ applicantIds })
  const contactsPictures = useContactsPictures({ contactIds })
  const usersPictures = useUsersPicture({ userIds })

  const allProfiles: ProfileHeadPicture[] = [
    ...(profilePictures ?? []),
    ...(applicantPictures
      .map((q) => q.data)
      .filter((p) => p != null) as ApplicantPicture[]),
    ...contactsPictures
      .map((q) => q.data)
      .filter((c): c is NonNullable<typeof c> => c != null)
      .map(
        (c) =>
          ({
            profilePicUrl: c.profilePicUrl,
            user: c.contact
          }) satisfies UserPicture
      ),
    ...(usersPictures
      .map((q) => q.data)
      .filter((u) => u != null) as UserPicture[])
  ]

  const getProfileHeadData = (profilePicture: ProfileHeadPicture) => {
    if ('applicant' in profilePicture) {
      return profilePicture.applicant
    }
    return profilePicture.user
  }

  const profiles = allProfiles.slice(0, maxProfilesCount)

  return (
    <Tooltip
      text={
        noTooltip
          ? null
          : tooltipText !== '' &&
              tooltipText !== null &&
              tooltipText !== undefined
            ? tooltipText
            : profiles
                .map((profilePicture) =>
                  formatName(getProfileHeadData(profilePicture))
                )
                .join(', ')
      }
      asChild
    >
      <div
        className={clsx(
          profilesListWrapper,
          className,
          align === 'left' && profilesListWrapperLeft,
          align === 'right' && profilesListWrapperRight
        )}
      >
        {profiles.map((profilePicture) => {
          const headData = getProfileHeadData(profilePicture)
          const initials = `${headData.firstname?.substring(0, 1) ?? ''}${
            headData.lastname?.substring(0, 1) ?? ''
          }`.toUpperCase()

          return (
            <div key={headData.id} className={profilesListItem}>
              {profilePicture.profilePicUrl && (
                <img
                  src={
                    profilePicture.profilePicUrl.slice(0, 1) === '/'
                      ? `${legacyUrl ?? ''}${profilePicture.profilePicUrl}`
                      : profilePicture.profilePicUrl
                  }
                  alt={formatName(headData)}
                  className={profilesListItemPicture}
                />
              )}
              {!profilePicture.profilePicUrl && (
                <div
                  className={clsx(
                    profilesListItemInitials,
                    profileColorClasses[getProfileColorIndex(initials)]
                  )}
                >
                  {initials}
                </div>
              )}
            </div>
          )
        })}
        {allProfiles.length > maxProfilesCount && (
          <div key={`more-${maxProfilesCount}`} className={profilesListItem}>
            <div
              className={clsx(
                profilesListItemInitials,
                profilesListItemInitialsNeutral
              )}
            >
              +{allProfiles.length - maxProfilesCount}
            </div>
          </div>
        )}
      </div>
    </Tooltip>
  )
}
